import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Medal, Pencil, Trash2, Link2, Plus, Loader2, MessageCircle, CreditCard } from "lucide-react";
import { FeeChargeWhatsappModal } from "@/components/fees/FeeChargeWhatsappModal";
import { PAYMENT_METHODS, paymentMethodLabel } from "@/lib/payment-methods";
import { brToday } from "@/lib/tz";

import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listFees, upsertFee, deleteFee, linkFeeToEvent, listEventsWithCategories,
} from "@/lib/api/summons.functions";


export const Route = createFileRoute("/_authenticated/m/taxa-campeonato")({
  component: TaxaCampeonatoPage,
});

type Fee = {
  id: string;
  type: "campeonato" | "arbitragem" | "material";
  name: string;
  amount: number;
  championship_id: string | null;
  due_date?: string | null;
  payment_method?: string | null;
  championships?: { name: string } | null;
};

const NO_EVENT = "__none__";
const DEFAULT_METHOD = "pix_comprovante";


function TaxaCampeonatoPage() {
  const listFn = useServerFn(listFees);
  const upsertFn = useServerFn(upsertFee);
  const deleteFn = useServerFn(deleteFee);
  const linkFn = useServerFn(linkFeeToEvent);
  const listEventsFn = useServerFn(listEventsWithCategories);

  const [fees, setFees] = useState<Fee[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<Fee | null>(null);
  const [formName, setFormName] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formType, setFormType] = useState<Fee["type"]>("campeonato");
  const [formEventId, setFormEventId] = useState<string>(NO_EVENT);
  const [formPaymentMethod, setFormPaymentMethod] = useState<string>(DEFAULT_METHOD);
  const [formDueDate, setFormDueDate] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Fee | null>(null);
  const [linking, setLinking] = useState<Fee | null>(null);
  const [linkEventId, setLinkEventId] = useState<string>(NO_EVENT);
  const [editingPayment, setEditingPayment] = useState<Fee | null>(null);
  const [editPayMethod, setEditPayMethod] = useState<string>(DEFAULT_METHOD);
  const [editPayDueDate, setEditPayDueDate] = useState<string>("");
  const [savingPayment, setSavingPayment] = useState(false);


  const reload = () => {
    setLoading(true);
    Promise.all([listFn({ data: {} }), listEventsFn()])
      .then(([f, e]: any[]) => { setFees(f.fees ?? []); setEvents(e.events ?? []); })
      .catch((e: any) => toast.error(e?.message || "Erro ao carregar"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const openCreate = () => {
    setEditing({ id: "", type: "campeonato", name: "", amount: 0, championship_id: null });
    setFormName(""); setFormAmount(""); setFormType("campeonato"); setFormEventId(NO_EVENT);
    setFormPaymentMethod(DEFAULT_METHOD); setFormDueDate(brToday());
  };
  const openEdit = (f: Fee) => {
    setEditing(f);
    setFormName(f.name); setFormAmount(String(f.amount));
    setFormType(f.type); setFormEventId(f.championship_id ?? NO_EVENT);
    setFormPaymentMethod(f.payment_method || DEFAULT_METHOD);
    setFormDueDate(f.due_date ?? brToday());
  };
  const save = async () => {
    if (!formName.trim()) { toast.error("Informe o nome da taxa"); return; }
    setSaving(true);
    try {
      await upsertFn({ data: {
        id: editing?.id || undefined,
        type: formType, name: formName.trim(), amount: Number(formAmount) || 0,
        championship_id: formEventId === NO_EVENT ? null : formEventId,
        payment_method: formPaymentMethod,
        due_date: formDueDate || null,
      } });
      toast.success(editing?.id ? "Taxa atualizada" : "Taxa criada");
      setEditing(null); reload();
    } catch (e: any) { toast.error(e?.message || "Erro ao salvar"); }
    finally { setSaving(false); }
  };
  const openEditPayment = (f: Fee) => {
    setEditingPayment(f);
    setEditPayMethod(f.payment_method || DEFAULT_METHOD);
    setEditPayDueDate(f.due_date ?? brToday());
  };
  const savePayment = async () => {
    if (!editingPayment) return;
    setSavingPayment(true);
    try {
      await upsertFn({ data: {
        id: editingPayment.id,
        type: editingPayment.type,
        name: editingPayment.name,
        amount: editingPayment.amount,
        championship_id: editingPayment.championship_id,
        payment_method: editPayMethod,
        due_date: editPayDueDate || null,
      } });
      toast.success("Forma de pagamento atualizada");
      setEditingPayment(null); reload();
    } catch (e: any) { toast.error(e?.message || "Erro ao salvar forma de pagamento"); }
    finally { setSavingPayment(false); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await deleteFn({ data: { id: confirmDel.id } });
      toast.success("Taxa excluída"); setConfirmDel(null); reload();
    } catch (e: any) { toast.error(e?.message || "Erro ao excluir"); }
  };
  const doLink = async () => {
    if (!linking) return;
    try {
      await linkFn({ data: { fee_id: linking.id, championship_id: linkEventId === NO_EVENT ? null : linkEventId } });
      toast.success("Vínculo atualizado"); setLinking(null); reload();
    } catch (e: any) { toast.error(e?.message || "Erro ao vincular"); }
  };

  const linked = fees.filter((f) => f.championship_id);
  const free = fees.filter((f) => !f.championship_id);
  const [charging, setCharging] = useState<Fee | null>(null);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2"><Medal className="h-6 w-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-bold">Taxa de Campeonato</h1>
            <p className="text-sm text-muted-foreground">Gerencie taxas vinculadas a eventos ou avulsas.</p>
          </div>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Nova taxa</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          <Section title="Taxas vinculadas a eventos" items={linked} onEdit={openEdit} onDelete={setConfirmDel} onLink={(f) => { setLinking(f); setLinkEventId(f.championship_id ?? NO_EVENT); }} onCharge={setCharging} onEditPayment={openEditPayment} empty="Nenhuma taxa vinculada a evento." />
          <Section title="Taxas avulsas" items={free} onEdit={openEdit} onDelete={setConfirmDel} onLink={(f) => { setLinking(f); setLinkEventId(NO_EVENT); }} onCharge={setCharging} empty="Nenhuma taxa avulsa cadastrada." />

        </>

      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar taxa" : "Nova taxa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={formType} onValueChange={(v: any) => setFormType(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="campeonato">Campeonato</SelectItem>
                  <SelectItem value="arbitragem">Arbitragem</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Ex.: Taxa Copinha 2026" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {["Taxa de Campeonato", "Taxa de Material", "Taxa de Arbitragem"].map((s) => (
                  <button key={s} type="button" onClick={() => setFormName(s)}
                    className="text-xs px-2 py-1 rounded-full border bg-muted hover:bg-primary/10 hover:border-primary/40 transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} />
            </div>
            <div>
              <Label>Evento vinculado (opcional)</Label>
              <Select value={formEventId} onValueChange={setFormEventId}>
                <SelectTrigger><SelectValue placeholder="Sem vínculo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_EVENT}>Sem vínculo (avulsa)</SelectItem>
                  {events.map((ev: any) => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={formPaymentMethod} onValueChange={setFormPaymentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vencimento</Label>
              <Input type="date" value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} />
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linking} onOpenChange={(o) => !o && setLinking(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vincular taxa a evento</DialogTitle></DialogHeader>
          <div>
            <Label>Evento</Label>
            <Select value={linkEventId} onValueChange={setLinkEventId}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_EVENT}>Remover vínculo (deixar avulsa)</SelectItem>
                {events.map((ev: any) => (
                  <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinking(null)}>Cancelar</Button>
            <Button onClick={doLink}>Salvar vínculo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir taxa?</AlertDialogTitle>
            <AlertDialogDescription>
              A taxa <strong>{confirmDel?.name}</strong> será removida definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editingPayment} onOpenChange={(o) => !o && setEditingPayment(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Forma de pagamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {editingPayment?.name} — <span className="font-medium">R$ {Number(editingPayment?.amount || 0).toFixed(2)}</span>
            </div>
            <div>
              <Label>Forma de pagamento</Label>
              <Select value={editPayMethod} onValueChange={setEditPayMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vencimento</Label>
              <Input type="date" value={editPayDueDate} onChange={(e) => setEditPayDueDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPayment(null)}>Cancelar</Button>
            <Button onClick={savePayment} disabled={savingPayment}>{savingPayment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FeeChargeWhatsappModal
        fee={charging}
        eventName={charging?.championships?.name}
        open={!!charging}
        onOpenChange={(v) => !v && setCharging(null)}
      />

    </div>
  );
}


function Section({
  title, items, empty, onEdit, onDelete, onLink, onCharge, onEditPayment,
}: {
  title: string; items: Fee[]; empty: string;
  onEdit: (f: Fee) => void; onDelete: (f: Fee) => void; onLink: (f: Fee) => void;
  onCharge: (f: Fee) => void; onEditPayment?: (f: Fee) => void;
}) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{title}</h2>
      {items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">{empty}</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => (
            <Card key={f.id}>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{f.type}</div>
                  </div>
                  <Badge variant="secondary">R$ {Number(f.amount).toFixed(2)}</Badge>
                </div>
                {f.championships?.name && (
                  <div className="text-xs text-muted-foreground">Evento: {f.championships.name}</div>
                )}
                <div className="text-xs text-muted-foreground">
                  Forma: {paymentMethodLabel(f.payment_method || "")}
                  {f.due_date && ` · Venc.: ${new Date(f.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}`}
                </div>

                <div className="flex flex-wrap gap-1 pt-2">
                  {f.championship_id && (
                    <Button size="sm" variant="default" onClick={() => onCharge(f)}>
                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> Cobrar
                    </Button>
                  )}
                  {f.championship_id && onEditPayment && (
                    <Button size="sm" variant="outline" onClick={() => onEditPayment(f)}>
                      <CreditCard className="h-3.5 w-3.5 mr-1" /> Forma de pagamento
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onEdit(f)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onLink(f)}>
                    <Link2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(f)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


